import { watch } from 'vue';
import { message } from 'ant-design-vue';
import XMSDKWebJs from '@xmiles/sdk-web-js';
import { optCorporateOrder } from '@/views/power/service';
import { CorporateTransferApplyStatus, type UploadReceiptParams } from '@/types/teamEdition.d';
import type { CorporatePaymentState } from '@/services/corporatePayment';

export interface CorporateReceiptState extends CorporatePaymentState {
  corporateReceiptLoading: boolean;
  corporateReceiptRequestId: number;
  corporateUploadedReceipt: UploadReceiptParams | null;
  modalOpen: boolean;
  currentStep: number;
  isUseCorporateTransfer: boolean;
}

function beginReceipt(state: CorporateReceiptState) {
  const requestId = ++state.corporateReceiptRequestId;
  state.corporateReceiptLoading = true;
  let active = true;
  // SDK 无取消接口；同步作废会话以隔离关窗再开、切单再返回。
  const stop = watch(() => [state.modalOpen, state.currentStep, state.isUseCorporateTransfer,
    state.corporateTransferOrderInfo.orderId], () => {
    active = false;
    state.corporateUploadedReceipt = null;
    finish();
  }, { flush: 'sync' });
  function finish() {
    stop();
    if (requestId === state.corporateReceiptRequestId) state.corporateReceiptLoading = false;
  }
  return { finish, isActive: () => active && requestId === state.corporateReceiptRequestId };
}

function sameReceipt(previous: UploadReceiptParams | null, data: UploadReceiptParams) {
  return previous?.innerOrderId === data.innerOrderId && previous.file === data.file &&
    previous.userBusinessName === data.userBusinessName && previous.userContactNumber === data.userContactNumber;
}

/** 保留 SDK 上传；业务确认成功才进入审核页。相同回执的业务更新重试不重复上传。 */
export async function submitCorporateReceipt(state: CorporateReceiptState, input: UploadReceiptParams) {
  const data = { ...input };
  if (state.corporateReceiptLoading || !state.modalOpen || state.currentStep !== 2 ||
      !state.isUseCorporateTransfer || !data.innerOrderId || data.innerOrderId !== state.corporateTransferOrderInfo.orderId) return;
  const session = beginReceipt(state);
  try {
    if (!sameReceipt(state.corporateUploadedReceipt, data)) {
      await XMSDKWebJs.uploadReceipt(data);
      if (!session.isActive()) return;
      state.corporateUploadedReceipt = data;
    }
    await optCorporateOrder({ transactionId: data.innerOrderId, orderStatus: 'WAIT_CORPORATE_CONFIRM',
      corporateName: data.userBusinessName, corporatePhone: data.userContactNumber });
    if (!session.isActive()) return;
    session.finish();
    state.corporateUploadedReceipt = null;
    state.corporateTransferApplyStatus = CorporateTransferApplyStatus.PENDING;
    state.currentStep = 3;
    message.success('上传成功');
  } catch {
    if (session.isActive()) message.error('回执提交失败，请重试');
  } finally {
    session.finish();
  }
}
